import React, { useCallback } from "react";

import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import AccessStatus from "@mapmycustomers/shared/types/entity/common/AccessStatus";
import FileTag from "@mapmycustomers/shared/types/entity/FileTag";
import File, { RawFile } from "@mapmycustomers/shared/types/File";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button, FileIcon, FileIconProps, Popconfirm } from "@mapmycustomers/ui";

import AddTagButton from "../AddTagButton";
import FileTags from "../FileTags";

import styles from "./FileRow.module.scss";

interface FileRowProps extends Pick<FileIconProps, "size"> {
  accessStatus?: AccessStatus;
  canDelete?: boolean;
  className?: string;
  deleteTooltip?: string;
  editingTags?: boolean;
  file: RawFile;
  onChange?: (file: RawFile) => void;
  onClick: (file: RawFile) => void;
  onDelete?: (file: RawFile) => void;
  onDownload: (file: RawFile) => void;
  supportsTags?: boolean;
}

const FileRow = ({
  accessStatus,
  canDelete,
  className,
  deleteTooltip,
  editingTags,
  file,
  onChange,
  onClick,
  onDelete,
  onDownload,
  size = "default",
  supportsTags,
}: FileRowProps) => {
  const intl = useIntl();

  const handleClick = useCallback(() => onClick(file), [file, onClick]);
  const handleDownload = useCallback(() => onDownload(file), [file, onDownload]);
  const handleDelete = useCallback(() => onDelete?.(file), [file, onDelete]);

  const handleTagsChange = useCallback(
    (tags: File["tags"]) => {
      onChange?.({ ...file, tags });
    },
    [file, onChange]
  );
  const handleRemoveTag = useCallback(
    (tagId: FileTag["id"]) => {
      onChange?.({ ...file, tags: file.tags?.filter(({ id }) => id !== tagId) });
    },
    [file, onChange]
  );

  return (
    <div
      className={cn(styles.container, className, {
        [styles.large]: size === "default",
        [styles.small]: size === "small",
      })}
      onClick={handleClick}
    >
      <div className={styles.file}>
        <div className={styles.title}>
          <FileIcon className={styles.fileIcon} file={file} size={size} />
          <div className={styles.name}>
            <Tooltip title={file.name}>{file.name}</Tooltip>
          </div>
        </div>
        <div className={styles.actions} onClick={stopEvents}>
          <Button
            icon={<FontAwesomeIcon icon={faDownload} />}
            iconOnly
            onClick={handleDownload}
            type="text"
          />
          {onDelete && (
            <Tooltip
              placement="left"
              title={
                deleteTooltip ??
                intl.formatMessage({
                  id: "component.fileRow.delete.disallowed",
                  defaultMessage:
                    "You do not have permission to use this feature. Please contact your admin to request a change.",
                  description: "A tooltip to show when user has no rights to delete a file",
                })
              }
              trigger={canDelete ? [] : ["hover"]}
            >
              <Popconfirm
                cancelText={intl.formatMessage({
                  id: "component.fileRow.deleteConfirmation.cancel",
                  defaultMessage: "Cancel",
                  description: "Cancel-confirmation text for other files section",
                })}
                description={intl.formatMessage({
                  id: "component.fileRow.delete",
                  defaultMessage: "Delete file?",
                  description: "Delete-confirmation text for other files section",
                })}
                disabled={!accessStatus?.delete || !canDelete}
                okText={intl.formatMessage({
                  id: "component.fileRow.deleteConfirmation.yes",
                  defaultMessage: "Yes",
                  description: "Yes-confirmation text for other files section",
                })}
                onConfirm={handleDelete}
              >
                <Button
                  danger
                  disabled={!accessStatus?.delete || !canDelete}
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  iconOnly
                  type="link"
                />
              </Popconfirm>
            </Tooltip>
          )}
        </div>
      </div>
      {supportsTags && (
        <>
          {file.tags && file.tags.length > 0 && (
            <FileTags onRemoveTag={editingTags ? handleRemoveTag : undefined} tags={file.tags} />
          )}
          {editingTags && <AddTagButton onChange={handleTagsChange} tags={file.tags} />}
        </>
      )}
    </div>
  );
};

export default FileRow;
